import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { API } from "./api";
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Demande from './pages/Demande';
import Historique from './pages/Historique';
import Validations from './pages/Validations';
import Planning from './pages/Planning';
import PlanningGlobal from './pages/PlanningGlobal';
import Soldes from './pages/Soldes';
import Admin from './pages/Admin';
import TT from './pages/TT';
import Absences from './pages/Absences';
import Profil from './pages/Profil';
import AuditLog from './pages/AuditLog';
import Statistics from './pages/Statistics';
import Annuel from './pages/Annuel';
import Layout from './components/Layout';
import { NotifProvider } from './components/NotifContext';
import './App.css';

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }, []);

  const login = (userData, userToken, userRefreshToken) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('token', userToken);
    localStorage.setItem('user', JSON.stringify(userData));
    if (userRefreshToken) localStorage.setItem('refreshToken', userRefreshToken);
  };

  // Refresh token silencieux
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) { logout(); return; }
    try {
      const res = await axios.post(API + '/auth/refresh', { refreshToken });
      const newToken = res.data.token;
      setToken(newToken);
      localStorage.setItem('token', newToken);
    } catch {
      logout();
    }
  }, [logout]);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (savedToken && savedUser) {
      const expiry = getTokenExpiry(savedToken);
      if (expiry && expiry < Date.now()) {
        // Token expiré au démarrage — tenter refresh
        refreshAccessToken();
      } else {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      }
    }
    setLoading(false);
  }, [refreshAccessToken]);

  // Vérification toutes les 5 min — refresh si token expire dans moins d'1h
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      const expiry = getTokenExpiry(token);
      if (expiry && expiry - Date.now() < 60 * 60 * 1000) {
        refreshAccessToken();
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token, refreshAccessToken]);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif', color: '#888' }}>Chargement...</div>;

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      <NotifProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={user ? <Layout /> : <Navigate to="/login" />}>
            <Route index element={<Dashboard />} />
            <Route path="demande" element={<Demande />} />
            <Route path="historique" element={<Historique />} />
            <Route path="validations" element={<Validations />} />
            <Route path="planning" element={<Planning />} />
            <Route path="planning-global" element={<PlanningGlobal />} />
            <Route path="soldes" element={<Soldes />} />
            <Route path="admin" element={<Admin />} />
            <Route path="tt" element={<TT />} />
            <Route path="absences" element={<Absences />} />
            <Route path="profil" element={<Profil />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="statistics" element={<Statistics />} />
            <Route path="annuel" element={<Annuel />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </NotifProvider>
    </AuthContext.Provider>
  );
}

export default App;
